import * as React from 'react'
import { useMemo } from 'react'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { FormikErrors, FormikTouched } from 'formik'
import FormHelperText from '@mui/material/FormHelperText'

import { useHandlers } from './hooks'
import { getStyles } from './styling'
import { IReferral } from '@sales-center/common'
import { useGetUserListOnMount } from '@user/store/users'
import { MuiValidationIcon } from '@sales-center/components'
import { ILeadFormModel } from '@user/components/Leads/AddLeadForm/typings'
import { TidalReferralPreview } from './content/preview/TidalReferralPreview'
import { ScrollToErrorMarker } from '@user/components/Formik/ScrollToErrorMarker'
import { UpdateReferralDrawer } from './UpdateReferralDrawer/UpdateReferralDrawer'
import { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunity/typings'
import { useGetEmployeeListOnMount } from '@user/store/employee/hooks/useGetEmployeeListOnMount'
import { TidalReferralActionButton } from './content/ReferralActionButton/TidalReferralActionButton'
import { useTheme } from '@mui/material/styles'

export enum ReferralFormVariant {
  Drawer,
  Inline,
}

export interface Props {
  billToCardMaxWidth?: string
  billToCardWidth?: string
  disabled?: boolean
  name?: string
  onChange: (referral: IReferral) => void
  padding?: string
  referral?: IReferral
  salesTeamId: string
  required?: boolean
  detailsView?: boolean
  entityErrors?:
    | FormikErrors<ILeadFormModel>
    | FormikErrors<IAddOpportunityFormModel>
  title?: string
  touched?:
    | FormikTouched<ILeadFormModel>
    | FormikTouched<IAddOpportunityFormModel>
  variant?: 'classic' | 'gray'
  tidalOnly?: boolean
  formVariant: ReferralFormVariant
}

export function TidalUpdateReferral(props: Props) {
  const {
    billToCardMaxWidth = '400px',
    billToCardWidth = '100%',
    disabled = false,
    onChange,
    referral,
    salesTeamId,
    required,
    entityErrors,
    title = 'Referral',
    touched,
    detailsView,
    name,
    formVariant,
  } = props

  const {
    onButtonClick,
    updateReferralDrawerOpen,
    onCloseReferralDrawer,
    handleSave,
  } = useHandlers({ onChange, formVariant })

  const showError = Boolean(entityErrors?.referral && touched?.referral)

  const employeeListStatus = useGetEmployeeListOnMount()
  const userListStatus = useGetUserListOnMount()
  const styles = getStyles(props, useTheme())
  const isInline = useMemo(() => {
    return formVariant === ReferralFormVariant.Inline
  }, [formVariant])

  return (
    <>
      <Grid container direction="column" sx={styles.container}>
        <Grid
          mb="12px"
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <Box sx={styles.title}>
              <Typography variant="subtitle3" display="inline">
                {title}
              </Typography>
              {required && !detailsView && (
                <MuiValidationIcon showError={false} />
              )}
            </Box>
          </Grid>

          <Grid>
            <TidalReferralActionButton
              disabled={disabled}
              onClick={onButtonClick}
              referral={referral}
              formVariant={formVariant}
              updateReferralDrawerOpen={isInline && updateReferralDrawerOpen}
              closeReferralDrawer={onCloseReferralDrawer}
            />
          </Grid>
        </Grid>

        {!isInline && (
          <TidalReferralPreview
            billToCardWidth={billToCardWidth}
            billToCardMaxWidth={billToCardMaxWidth}
            employeeListStatus={employeeListStatus}
            referral={referral}
            userListStatus={userListStatus}
            variant="entityAdd"
          />
        )}

        <UpdateReferralDrawer
          open={updateReferralDrawerOpen || Boolean(isInline && referral)}
          onClose={onCloseReferralDrawer}
          onSave={handleSave}
          referral={referral}
          salesTeamId={salesTeamId}
          required={required}
          formVariant={formVariant}
        />

        {isInline && !referral && !updateReferralDrawerOpen && (
          <TidalReferralPreview
            billToCardWidth={billToCardWidth}
            billToCardMaxWidth={billToCardMaxWidth}
            employeeListStatus={employeeListStatus}
            referral={referral}
            userListStatus={userListStatus}
            variant="entityAdd"
          />
        )}
      </Grid>

      {!referral && showError && entityErrors && entityErrors.referral && (
        <FormHelperText error required>
          {entityErrors.referral}
        </FormHelperText>
      )}
      {name && <ScrollToErrorMarker fieldName={name} />}
    </>
  )
}
